import Button from "@/v2-backoffice-client/components/button"
import { DotText } from "@/v2-backoffice-client/components/dot"
import TenantPicker from "@/v2-backoffice-client/tenant/TenantPicker"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import {
  sectionMatchStatus,
  type InvitationSection,
} from "@/v2-backoffice-client/invitation/create/invitation.create.sections"

export type InvitationCreateTeamSlotProps = {
  section: InvitationSection
  onSelectTenant(tenant: TenantFragment | null): void
  onVerify(): void
}

/**
 * The team cell of one invite section: the resolved team with its match
 * status (auto-matches take an operator "verified" stamp), the pick-one
 * prompt for ambiguous names, and the will-create notice for unmatched
 * ones — plus the picker whenever no team is resolved.
 */
function InvitationCreateTeamSlot(props: InvitationCreateTeamSlotProps) {
  const { section, onSelectTenant, onVerify } = props
  const status = sectionMatchStatus(section)

  if (section.tenant) {
    return (
      <>
        <div className="invitation-create-form-tenant-selected">
          <span>{section.tenant.Name}</span>
          <Button
            type="button"
            variant="ghost"
            size="tiny"
            iconSrc="cross-small"
            aria-label="Change team"
            onClick={() => {
              onSelectTenant(null)
            }}
          />
        </div>
        {status === "verified" && (
          <DotText variant="success-1" className="text-xs">
            Verified
          </DotText>
        )}
        {status === "matched" && (
          <>
            <DotText variant="success-2" className="text-xs">
              Matched automatically
            </DotText>
            <Button
              type="button"
              variant="outline"
              size="tiny"
              iconSrc="check"
              onClick={onVerify}
            >
              Verify
            </Button>
          </>
        )}
      </>
    )
  }

  return (
    <>
      <TenantPicker
        enableCreate
        required={status === "manual" || status === "ambiguous"}
        name={`section-${section.key}-tenant`}
        value={null}
        defaultSearch={section.imported?.teamName}
        aria-label="Team"
        onSelect={onSelectTenant}
      >
        {status === "create"
          ? "Pick an existing team instead..."
          : section.imported?.teamName
            ? `Pick a team for “${section.imported.teamName}”...`
            : "Select or create team..."}
      </TenantPicker>
      {status === "ambiguous" && section.imported && (
        <DotText variant="warning" className="text-xs">
          {section.imported.candidates.length} teams share “
          {section.imported.teamName}” — pick one
        </DotText>
      )}
      {status === "create" && section.imported && (
        <DotText variant="info" className="text-xs">
          No match — sending creates team “{section.imported.teamName}”
          {section.imported.suggestedCustomer
            ? ` under customer “${section.imported.suggestedCustomer.Name ?? ""}”`
            : " and its customer"}
        </DotText>
      )}
    </>
  )
}

export default InvitationCreateTeamSlot
